import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useQueries, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@nexus/ui";
import { toast } from "sonner";
import { SCRIPT_REVIEW_PROMPTS } from "@nexus/contracts";
import { promptsListQueryOptions, promptDetailQueryOptions, promptsKeys } from "@/lib/queries";
import type { PromptDto } from "@/lib/queries";
import { createPromptVersion } from "@/features/prompts/api/prompts.api";
import { PromptEditorSheet } from "@/features/prompts/components/PromptEditorSheet";

/**
 * Hub section (below "Recent reviews") to edit the full per-step AI prompts the
 * pipeline uses. Reuses the global Prompts system (versioned + revertable):
 * these are 5 global prompts under the `script-review.*` namespace. The whole
 * template is editable — instruction, data layout, and output rules. The worker
 * fills the {{scenes}} / {{transcript}} variables at review time, and the Gemini
 * calls enforce output shape via schemas, so an edit can shift accuracy but
 * can't break parsing.
 */
export function ScriptReviewPromptsSection() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [editingId, setEditingId] = useState<string | null>(null);

  const { data: prompts = [], isLoading } = useQuery(
    promptsListQueryOptions({ tenantId: "__global__" }),
  );
  const byUsedIn = new Map(prompts.map((p) => [p.usedIn, p]));
  const editingPrompt = prompts.find((p) => p.id === editingId) ?? null;

  // The list endpoint truncates latestVersion.content to a 100-char preview, so
  // judge "customized" from the untruncated detail endpoint (shared query cache).
  const detailQueries = useQueries({
    queries: SCRIPT_REVIEW_PROMPTS.map((def) => byUsedIn.get(def.key))
      .filter((p): p is PromptDto => !!p)
      .map((p) => promptDetailQueryOptions(p.id)),
  });
  const fullContentById = new Map<string, string>();
  for (const q of detailQueries) {
    if (q.data?.latestVersion) fullContentById.set(q.data.id, q.data.latestVersion.content);
  }

  const invalidate = () => qc.invalidateQueries({ queryKey: promptsKeys.all });

  const resetMut = useMutation({
    mutationFn: ({ id, content, variables }: { id: string; content: string; variables: string[] }) =>
      createPromptVersion(id, { content, variables }),
    onSuccess: () => {
      toast.success(
        t("scriptReview.prompts.resetDone", "Reset to default — saved as a new version."),
      );
      void invalidate();
    },
    onError: () => toast.error(t("scriptReview.prompts.resetFailed", "Failed to reset prompt")),
  });

  return (
    <section className="flex flex-col gap-3">
      <div className="flex flex-col gap-0.5">
        <h2 className="text-sm font-medium text-muted-foreground">
          {t("scriptReview.prompts.heading", "AI pipeline prompts")}
        </h2>
        <p className="text-xs text-muted-foreground">
          {t(
            "scriptReview.prompts.hint",
            "Edit the full prompt for each analysis step to iterate on accuracy. The scene list and video transcript are inserted automatically at review time wherever the template references them. Changes are versioned and apply to every future run.",
          )}
        </p>
      </div>

      <div className="overflow-hidden rounded-lg border border-border">
        {SCRIPT_REVIEW_PROMPTS.map((def) => {
          const prompt = byUsedIn.get(def.key) ?? null;
          const version = prompt?.latestVersion?.version;
          const fullContent = prompt ? fullContentById.get(prompt.id) : undefined;
          // Undefined while the detail query loads → treat as default (don't flash "Customized").
          const customized = fullContent !== undefined && fullContent !== def.defaultContent;
          return (
            <div
              key={def.key}
              className="flex items-center justify-between gap-4 border-b border-border px-4 py-3 last:border-0"
            >
              <div className="flex min-w-0 flex-col">
                <span className="truncate text-sm font-medium">{def.label}</span>
                <span className="truncate text-xs text-muted-foreground">{def.description}</span>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <span className="text-xs text-muted-foreground">
                  {prompt
                    ? customized
                      ? t("scriptReview.prompts.customized", "Customized · v{{n}}", { n: version })
                      : t("scriptReview.prompts.default", "Default")
                    : isLoading
                      ? "…"
                      : t("scriptReview.prompts.default", "Default")}
                </span>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={!prompt}
                  onClick={() => prompt && setEditingId(prompt.id)}
                >
                  {t("scriptReview.prompts.edit", "Edit")}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={!prompt || !customized || resetMut.isPending}
                  onClick={() =>
                    prompt &&
                    resetMut.mutate({
                      id: prompt.id,
                      content: def.defaultContent,
                      variables: [...def.variables],
                    })
                  }
                >
                  {t("scriptReview.prompts.reset", "Reset to default")}
                </Button>
              </div>
            </div>
          );
        })}
      </div>

      <PromptEditorSheet
        open={!!editingId}
        onOpenChange={(open) => {
          if (!open) setEditingId(null);
        }}
        promptId={editingId}
        prompt={editingPrompt}
        mode="edit"
        onSaved={() => void invalidate()}
      />
    </section>
  );
}
